import React, { useRef, useState, useEffect } from "react";
import AudioWaveform from "./AudioWaveform";
import styles from "./ChatJessAudioPlayer.module.scss";
import logger from "../../../../utils/logger";

interface ChatJessAudioPlayerProps {
  audioUrl: string;
  audioBlob?: Blob;
  waveformData?: number[]; // Dados da waveform pré-processados
  isFromMe?: boolean;
  isDarkTheme?: boolean;
  duration: number;
  className?: string;
}

const ChatJessAudioPlayer: React.FC<ChatJessAudioPlayerProps> = ({
  audioUrl,
  audioBlob,
  waveformData,
  isFromMe = false,
  isDarkTheme = true,
  duration,
  className = "",
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [playbackRate, setPlaybackRate] = useState(1);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setIsLoading(false);
    };

    const handleTimeUpdate = () => {
      if (isFinite(audio.currentTime)) {
        setCurrentTime(audio.currentTime);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      const oldPlaybackRate = audio.playbackRate;
      setPlaybackRate(oldPlaybackRate);
      audio.load();
      audio.playbackRate = oldPlaybackRate;
    };

    const handleCanPlay = () => {
      setIsLoading(false);
    };

    const handleLoadedData = () => {
      setIsLoading(false);
    };

    const handleError = (e: Event) => {
      logger.error("🎵 Audio error details:", {
        audioUrl,
        error: e,
        readyState: audio.readyState,
        networkState: audio.networkState,
        audioType: audioBlob?.type || "unknown",
      });
      setIsLoading(false);
    };

    // Adicionar mais listeners para melhor compatibilidade
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("loadeddata", handleLoadedData);
    audio.addEventListener("canplay", handleCanPlay);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("error", handleError);

    // Forçar carregamento se necessário
    if (audio.readyState >= 1) {
      handleLoadedMetadata();
    } else {
      // Forçar carregamento inicial
      audio.load();
    }

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("loadeddata", handleLoadedData);
      audio.removeEventListener("canplay", handleCanPlay);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("error", handleError);
    };
  }, [audioUrl]);

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio || isLoading) return;

    try {
      if (isPlaying) {
        audio.pause();
        setIsPlaying(false);
      } else {
        setIsPlaying(true);

        if (audio.readyState === 0) {
          audio.load();
        }

        await audio.play();
      }
    } catch (error) {
      setIsPlaying(false);
      logger.error("🎵 Playback failed:", error);
    }
  };

  const handleSeek = (time: number) => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    // Validações robustas
    if (!isFinite(time) || time < 0) {
      return;
    }

    // Aguardar até que a duração esteja disponível
    if (!isFinite(audio.duration) || audio.duration <= 0) {
      return;
    }

    // Garantir que o tempo não exceda a duração
    const validTime = Math.max(0, Math.min(time, audio.duration));

    // Validar novamente antes de definir
    if (!isFinite(validTime)) {
      return;
    }

    try {
      audio.currentTime = validTime;
      setCurrentTime(validTime);
    } catch (error) {
      logger.error("🎵 Error seeking audio:", error);
    }
  };

  const togglePlaybackRate = () => {
    const audio = audioRef.current;
    if (!audio) return;

    const rates = [1, 1.2, 1.5, 2];
    const currentIndex = rates.indexOf(playbackRate);
    const nextRate = rates[(currentIndex + 1) % rates.length];

    setPlaybackRate(nextRate);
    audio.playbackRate = nextRate;
  };

  const formatTime = (seconds: number): string => {
    // Garantir que temos um número válido
    if (!isFinite(seconds) || seconds < 0) return "0:00";

    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);

    // Validar se os cálculos resultaram em números válidos
    if (!isFinite(mins) || !isFinite(secs)) return "0:00";

    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Função para obter duração total formatada
  const getTotalDuration = (): string => {
    return formatTime(duration);
  };

  const themeClass = isDarkTheme ? styles.dark : styles.light;
  const sideClass = isFromMe ? styles.fromMe : styles.fromOther;

  return (
    <div
      className={`${styles.audioPlayer} ${themeClass} ${sideClass} ${className}`}
    >
      {/* hidden audio element using <source> to avoid unsupported source error */}
      <audio
        ref={audioRef}
        src={audioUrl}
        style={{ display: "none" }}
        preload="metadata"
      />

      <div className={styles.playerControls}>
        {/* Botão Play/Pause */}
        <button
          className={styles.playButton}
          onClick={togglePlay}
          disabled={isLoading}
          aria-label={isPlaying ? "Pausar" : "Reproduzir"}
        >
          {isLoading ? (
            <div className={styles.loadingSpinner}></div>
          ) : (
            <i className={`fas ${isPlaying ? "fa-pause" : "fa-play"}`}></i>
          )}
        </button>

        {/* Layout simplificado: Waveform | Duração total */}
        <div className={styles.audioContent}>
          {/* Waveform (centro) */}
          <div className={styles.waveformSection}>
            <AudioWaveform
              audioBlob={audioBlob}
              audioUrl={audioUrl}
              waveformData={waveformData}
              isFromMe={isFromMe}
              isDarkTheme={isDarkTheme}
              currentTime={currentTime}
              duration={duration}
              onSeek={handleSeek}
              className={styles.waveform}
            />
          </div>

          {/* Duração total (à direita) */}
          <span className={styles.totalDuration}>{getTotalDuration()}</span>
        </div>

        {/* Botão de velocidade (opcional) */}
        <button
          className={styles.speedButton}
          onClick={togglePlaybackRate}
          title={`Velocidade: ${playbackRate}x`}
        >
          {playbackRate}x
        </button>
      </div>
    </div>
  );
};

export default ChatJessAudioPlayer;
